import { Component, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { distinctUntilChanged, filter, finalize, take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';
import { GlobalFunctions, imagePath, imgTypes, SnackType } from '@global/GlobalFunctions';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { IOrganisationsHierarchy } from '@services/organisation-data.service';
import { Location } from '@angular/common';
import { SelectEventArgs } from '@syncfusion/ej2-angular-navigations';
import { AssetDataPoint } from '@services/datapoints-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Rule } from '@services/rules-data.service';
import { MatDialog } from '@angular/material/dialog';
import { Account } from '@services/accounts.service';
import { RuleEvent } from '@services/events-data.service';
import { WatchListComponent } from '@components/watch-list/watch-list.component';
import { EditAssetDatapointsComponent, EditAssetSettingsDialogData } from 'src/app/dialogs/edit-asset-datapoints/edit-asset-datapoints.component';
import { RuleListComponent, RuleListDialogData } from 'src/app/dialogs/rule-list/rule-list.component';
import { CreateActionComponent, createActionDialogData } from 'src/app/dialogs/create-action/create-action.component';
import { Action, ActionsDataService } from '@services/actions-data.service';
import { ActionListDialogData, AssetActionsComponent } from 'src/app/dialogs/asset-actions/asset-actions.component';
import { AssetHistoryComponent } from 'src/app/dialogs/asset-history/asset-history.component';
import { AssetModelsComponent } from 'src/app/dialogs/asset-models/asset-models.component';

/**
 * Component
 */
@Component({
	selector: 'app-asset-details',
	templateUrl: './asset-details.component.html',
	styleUrls: ['./asset-details.component.scss']
})
export class AssetDetailsComponent implements OnDestroy {

	/** Property to hold the sensor name */
	@Input() sensorName: string = "";

	/** Selected Account ID */
	public selectedAccountUID: string = ""

	/** Selected Organisation ID */
	public selectedOrganisationUID: string = ""

	/** Selected Sub Location */
	public selectedSubLocation: string = ""

	/** Selected Hierarchical Location */
	public selectedHierarchicalLocation: IOrganisationsHierarchy;

	/** Parameter Asset ID */
	public paramAssetID: number;

	/** Selected Asset */
	public selectedAsset: LevelAsset;

	public locationUID: string = "";

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** fromEvent - true if we have clicked from a rule event to this asset */
	public fromEvent: boolean = false;

	public timeFrom: string = "";
	public timeTo: string = "";

	// /** hierarchical Data */
	// private hierarchicalData$: Observable<string>;
	// /** hierarchical Data Subject*/
	// private hierarchicalDataSubject: BehaviorSubject<string>;

	/** hierarchicalData */
	public hierarchicalData: IOrganisationsHierarchy[];

	public correctLocation: boolean = false;

	// public dataPoints: AssetDataPoint[];
	public chartDataPoints: AssetDataPoint[];

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.selectedAccountUID, imgTypes.sensors).toLowerCase()
	}

	/** currentUser */
	public currentUser: WebAppUser;

	public assetRules: Rule[];

	/** account */
	public account: Account;

	public fields: Object = { text: 'RuleText', id: 'RuleEventID' };

	public ruleEvents: RuleEvent[] = [];

	public filteredRuleEvents: RuleEvent[] = [];

	public selectedRule: Rule;

	/** Used when explaining the rules */
	public conditions: { text: string, value: string }[] = [
		{ text: "More Than", value: ">" },
		{ text: "More Than Or Equal To", value: ">=" },
		{ text: "Less Than", value: "<" },
		{ text: "Less Than Or Equal To", value: "<=" }
		// { text: "Equal To", value: "=" },
		// { text: "Not Equal To", value: "<>" },
	];

	public refreshChart: boolean

	/**
	 * Default Constructr
	 * @param route route
	 * @param router 
	 * @param assetsDataService AssetsDataService
	 * @param siteSubscriptions siteSubscriptions
	 * @param actionsDataService actionsDataService
	 * @param dialog dialog
	 * @param authenticationService 
	 * @param globalFunctions globalFunctions
	 * @param location location
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		public assetsDataService: AssetsDataService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		public dialog: MatDialog,
		private authenticationService: AuthenticationService,
		private globalFunctions: GlobalFunctions,
		private location: Location
	) {
		// this.hierarchicalDataSubject = new BehaviorSubject<string>("");
		// this.hierarchicalData$ = this.hierarchicalDataSubject.asObservable();

		this.globalFunctions.displaySpinner(true);
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedSubLocation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedHierarchicalLocation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParams.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedAccount, selectedOrganisationUID, selectedSubLocation,
				selectedHierarchicalLocation, currentUser, queryParams, paramMap]) =>
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID && !!selectedSubLocation &&
				!!selectedHierarchicalLocation && !!currentUser && paramMap.has('assetID') && !!queryParams)
		).subscribe({
			next: ([selectedAccountGUID, selectedAccount, selectedOrganisationUID, selectedSubLocation, selectedHierarchicalLocation, currentUser, queryParams, paramMap]) => {
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;
				this.selectedSubLocation = selectedSubLocation;
				this.selectedHierarchicalLocation = selectedHierarchicalLocation;
				this.currentUser = currentUser;
				this.account = selectedAccount;
				this.account = this.siteSubscriptions.normalizeFeatures(this.account) as Account;

				if (queryParams['events']) {
					this.fromEvent = queryParams['events'];
				}
				let assetID = parseInt(paramMap.get('assetID') ?? "", 0);
				/** if the asset hasnt changed but there are no query params we dont make any network requests,
				 * used because changing query params and params at the same time causes everything to fire twice
				 */
				let dontCheck: boolean = false;
				if (this.paramAssetID == assetID && !queryParams["from"]) {
					dontCheck = true;
				}
				this.paramAssetID = assetID;

				if (this.paramAssetID && !dontCheck) {
					this.sensorName = this.paramAssetID.toString();
					if (!this.selectedAsset) {
						this.getOrganisationAsset(this.selectedAccountUID, this.selectedOrganisationUID, this.paramAssetID);
					}
					else if (this.selectedAsset && this.selectedAsset.AssetID != this.paramAssetID) {
						this.getOrganisationAsset(this.selectedAccountUID, this.selectedOrganisationUID, this.paramAssetID);
					}
				}

				this.timeFrom = queryParams['from'];
				this.timeTo = queryParams['to'];

				// this.getFavouriteAssets();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/** goToFunctionalLocation */
	goToFunctionalLocation(assetID: number) {
		this.siteSubscriptions.SetSelectedAsset(this.selectedAsset);
		this.router.navigate([`/${this.selectedAccountUID}/organisation/${this.selectedOrganisationUID}/location/${this.selectedAsset.ParentUID}/asset/${assetID}`])
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Called as a result of the Show On Chart slide toggle changing, used
	 * to patch the asset datapoints table
	 * @param data
	 * @param event
	 */
	patchAssetDataPoint(data: any, event: MatSlideToggleChange) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let patchAssetDataPoint: AssetDataPoint = data;
		if (patchAssetDataPoint) {			
			if (event.checked) {
				patchAssetDataPoint.ShowOnChart = true
			} else {
				patchAssetDataPoint.ShowOnChart = false
			}
			this.assetsDataService.patchAssetDataPoint$(this.selectedAccountUID, this.selectedOrganisationUID, this.selectedAsset.AssetID,
				patchAssetDataPoint.DataPointID, patchAssetDataPoint).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						// this.numberRND = Math.random()
						// this.GetDataPoints(this.selectedAsset)
						if (event.checked) {
							this.globalFunctions.DisplaySnackBar({
								message: "DataPoint Unhidden On Chart",
								type: SnackType.Info,
								duration: 1000
							});
						} else {
							this.globalFunctions.DisplaySnackBar({
								message: "DataPoint Hidden On Chart",
								type: SnackType.Info,
								duration: 1000
							});
						}
					}
				});
		}
	}

	/**
	 * getReadableTime
	 * @param epoch
	 * @returns
	 */
	getReadableTime(epoch: string): string {
		let numEpoch: number = parseInt(epoch)
		return convertEpochToDateString(numEpoch)
	}

	/**
	 * goBack
	 */
	goBack(): void {
		this.location.back();
	}

	/**
	 * Used to prevent tabs changing when graph touched on mobile view
	 */
	public select(e: SelectEventArgs) {
		if (e.isSwiped) {
			e.cancel = true;
		}
	}

	/**
	 * findLocationUID
	 * @param hierarchy
	 */
	findLocationUID(assetID: number): Promise<string> {
		return new Promise((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.assetsDataService.getLocationUIDfromAssetID$(this.selectedAccountUID, this.selectedOrganisationUID, assetID).pipe(
				takeUntil(fetchDataSubject),
				distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					resolve(response[0].Data); // Return the desired value
				},
				error: (error) => {
					console.error(error);
					this.globalFunctions.HandleError(error, "dashboardWatchList: error patching asset");
					resolve(""); // Return an empty string on error
				}
			});
		});
	}

	/**
	 * Get Organisation Asset
	 * @param selectedAccountUID
	 * @param selectedOrganisationUID
	 * @param paramAssetID
	 */
	async getOrganisationAsset(selectedAccountUID: string, selectedOrganisationUID: string, paramAssetID: number) {

		try {
			const asset = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(selectedAccountUID, selectedOrganisationUID, paramAssetID).pipe(
					filter((asset) => !!asset),
					take(1)
				)
			);

			if (asset) {
				this.selectedAsset = asset;
				// this.GetDataPoints(this.selectedAsset);
				if (this.router.url.includes(this.selectedAsset.ParentUID)) {
					this.correctLocation = true;
				}
				this.siteSubscriptions.SetSelectedAsset(this.selectedAsset);
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false);			
		}

		this.globalFunctions.displaySpinner(false);
	}

	/**
	 * Converts operator symbols into readable strings
	 * @param value
	 * @returns
	 */
	getTextFromValue(value: string): string | undefined {
		const condition = this.conditions.find(cond => cond.value.toLowerCase() === value.toLowerCase());
		return condition ? condition.text : undefined;
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}


	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/** getDate */
	getDate(timestamp: number): string {
		return convertEpochToDateString(timestamp)
	}

	/** GoToEvent */
	GoToEvent(event: RuleEvent) {
		this.router.navigate([`./${this.selectedAccountUID}/organisation/${this.selectedOrganisationUID}/event/${event.RuleEventID}`], {
			queryParams: { eventType: '2' }
		});
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFiltering(event: any): void {
		event.cancel = true;
		const query = event.text.toLowerCase();
		this.filteredRuleEvents = this.ruleEvents.filter(data => {
			const dateStr = `${convertEpochToDateString(data.TimeStamp)}`.toLowerCase();
			const assetInfo = `${data.AssetName || ''}`.toLowerCase();
			const ruleEventID = data.RuleEventID.toString().toLowerCase();

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${assetInfo} ${ruleEventID}`;
			return combinedStr.includes(query);
		});
	}

	/** popUpWatchList
	 *  Open watch list as a pop up dialog
	 */
	popUpWatchList(): void {
		const dialogRef = this.dialog.open(WatchListComponent);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	/** popUpDatapoints
	 *  Open datapoints list as a pop up dialog
	 */
	popUpDatapoints(): void {
		let data: EditAssetSettingsDialogData = {
			assetItem: this.selectedAsset,
			selectedAccountUID: this.selectedAccountUID,
			OrganisationUID: this.selectedOrganisationUID,
			isAdmin: this.currentUser.AdminSite || this.account.Admin
		}

		const dialogRef = this.dialog.open(EditAssetDatapointsComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			this.siteSubscriptions.RefreshChart(true);
		});
	}

	/** popUpRules
	 *  Open rules list as a pop up dialog
	 */
	popUpRules(): void {
		let data: RuleListDialogData = {
			asset: this.selectedAsset,
			isAdmin: this.currentUser.AdminSite || this.account.Admin,
			currentUser: this.currentUser,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
		}

		const dialogRef = this.dialog.open(RuleListComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	/**
	 * popUpActionList
	 */
	popUpActionList(): void {
		let data: ActionListDialogData = {
			asset: this.selectedAsset,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
		}

		if (this.currentUser) {
			data.currentUser = this.currentUser;
		}

		const dialogRef = this.dialog.open(AssetActionsComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	public favouriteAssets: LevelAsset[] = [];

	/**
	 * getFavouriteAssets
	 */
	getFavouriteAssets() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getUserFavouriteAssets$(this.selectedAccountUID, this.selectedOrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				if (result[0].Data) {
					this.favouriteAssets = JSON.parse(result[0].Data);
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getUserFavouriteAssets$", false); },
		});
	}

	/**
	 * checkAssetInFavourites
	 * @param asset asset
	 * @returns
	 */
	checkAssetInFavourites(asset: LevelAsset): boolean {
		let arr: number[] = [];
		this.favouriteAssets.forEach(_asset => {
			arr.push(_asset.AssetID);
		})
		if (arr.includes(asset.AssetID)) {
			return true;
		} else {
			return false;
		}
	}

	/**
	 * createAction
	 */
	createAction() {
		let dialogData = <createActionDialogData>{
			assetItem: this.selectedAsset,
			isAttachedtoEvent: false
		}

		const dialogRef = this.dialog.open(CreateActionComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			if (!result) {
				// this.createAction();
			} else {
				if (!result.skipAction) {
					let newAction: Action = {
						ActionID: 0,
						ActionType: result.actionType,
						AssetID: this.selectedAsset.AssetID,
						AssetName: `${this.selectedAsset.Name} - ${this.selectedAsset.Description}`,
						EventID: 0,
						TimeLineID: 0,
						Timestamp: 0,
						Completed: false,
						Details: result.details,
						CompletedTimestamp: 0,
						CompletedByUserID: '',
						CreatedByUserSub: '',
						AssignedToUserSub: result.assignedToUser,
						WorkOrderReference: result.workOrderReference,
						TargetCompletionTimestamp: result.targetCompletionTimestamp.getTime(),
						DisabledRules: result.ruleList ? result.ruleList.toString() : ''
					}
					let disable;
					if (result.ruleList && result.ruleList.length > 0) {
						disable = true;
					}

					const fetchDataSubject = new Subject<void>();
					this.actionsDataService.postAction$(this.selectedAccountUID, this.selectedOrganisationUID, newAction, disable).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((action) => !!action),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
						})
					).subscribe({
						next: (action) => {
							this.globalFunctions.DisplaySnackBar({
								message: "Action Created",
								type: SnackType.Info,
								duration: 1000
							});
						}
					});
				}
			}
		});
	}

	/**
	 * compareAsset
	 */
	compareAsset() {
		this.router.navigate([`${this.selectedAccountUID}/organisation/${this.selectedOrganisationUID}/compare`],
			{
				queryParams:
				{
					asset1: this.selectedAsset.AssetID
				}
			}
		);
	}

	/**
	 * popUpHistory 
	 * functionality based on issue #4
	 */
	popUpHistory(): void {
		let data = {
			asset: this.selectedAsset,
			accountGUID: this.selectedAccountUID,
			organisationUID: this.selectedOrganisationUID,
		}

		const dialogRef = this.dialog.open(AssetHistoryComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	// /**
	//  * createDistribution
	//  */
	// createDistribution() {
	// 	let data = {
	// 		asset: this.selectedAsset
	// 	}

	// 	const dialogRef = this.dialog.open(CreateAssetDistributionComponent
	// 		, { data: data }
	// 	);

	// 	dialogRef.afterClosed().pipe(
	// 		filter(result => !!result)
	// 	).subscribe(result => {
	// 		if (!result.isAutomatic) {
	// 			this.postDistributionData(result.distributionData, result.remind, result.remind ? result.remindAfter : 0);
	// 		} else {
	// 			this.postDistributionData(result.distributionData, false, 0, true, result.automaticWindowWidth);
	// 		}
	// 	});
	// }

	// /**
	//  * postDistributionData
	//  * @param data data 
	//  */
	// postDistributionData(data: any, remind: boolean = false, remindAfter: number = 0, isAutomatic: boolean = false, automaticWindowWidth: number = 0) {
	// 	// Create a local Subject to control the subscription lifecycle
	// 	const fetchDataSubject = new Subject<void>();

	// 	this.assetsDataService.postAssetDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, this.selectedAsset.AssetID, data, isAutomatic, automaticWindowWidth).pipe(
	// 		takeUntil(fetchDataSubject), distinctUntilChanged(),
	// 		filter((response) => !!response),
	// 		finalize(() => {
	// 			// Ensure the subscription is terminated and the subject is completed
	// 			fetchDataSubject.next();
	// 			fetchDataSubject.complete();
	// 		})
	// 	).subscribe({
	// 		next: (result) => {
	// 			if (result[0].Result.includes("OK")) {
	// 				this.globalFunctions.DisplaySnackBar({
	// 					message: "Asset Distribution Created",
	// 					type: SnackType.Info,
	// 					duration: 1000
	// 				});
	// 			}
	// 		},
	// 		error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
	// 	});
	// }

	/**
	 * openAssetModels
	 */
	openAssetModels() {
		let data = {
			asset: this.selectedAsset
		}

		const dialogRef = this.dialog.open(AssetModelsComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}
}
